import { Fragment, type ReactNode } from 'react'

import StatusMessage from '@/ui/components/StatusMessage/StatusMessage'
import CollapsibleStatusPanel from './CollapsibleStatusPanel'
import StatusItem from './StatusItem'
import useChatStatusPhases from './useChatStatusPhases'
import type { ChatStatusMessageProps, ChatStatusMessageItem, ChatStatusMessageItemKind } from './types/ChatStatusMessageTypes'

import './ChatStatusMessage.scss'

interface ChatStatusMessageItemDisplayProps extends ChatStatusMessageItem {
    showConnectorLine?: boolean
    isConnectedToFollowUp?: boolean
    withoutVisual?: boolean
}

const ChatStatusMessageItemComponent = ({
    type, visual, message, state = 'done',
    loadingPhrases = [], messageSize,
    showConnectorLine = false,
    isConnectedToFollowUp = false,
    content,
    withoutVisual = false,
}: ChatStatusMessageItemDisplayProps) => {
    const cyclingPhrase = useChatStatusPhases(state, loadingPhrases)
    const displayMessage = cyclingPhrase ?? message

    return (
        <StatusItem state={state} type={type} connectToFollowUp={isConnectedToFollowUp}>
            <StatusMessage
                state={state}
                type={type}
                visual={withoutVisual ? { kind: 'none' } : visual}
                message={displayMessage}
                messageSize={messageSize}
            />
            {showConnectorLine && <div className="chat-status-message-item__connector" />}
            {content && <div className="chat-status-message-item__content">{content}</div>}
        </StatusItem>
    )
}

interface RendererProps {
    item: ChatStatusMessageItem
    isLast: boolean
    connectFollowUp: boolean
    withoutVisual: boolean
}

// One renderer per kind of item: a new kind is a new entry here
const RENDERERS: Record<ChatStatusMessageItemKind, (props: RendererProps) => ReactNode> = {
    row: ({ item, isLast, connectFollowUp, withoutVisual }) => (
        <ChatStatusMessageItemComponent
            {...item}
            showConnectorLine={!isLast}
            isConnectedToFollowUp={connectFollowUp}
            withoutVisual={withoutVisual}
        />
    ),
    group: ({ item, isLast, connectFollowUp }) => (
        <StatusItem state={item.state} type={item.type} connectToFollowUp={connectFollowUp}>
            <CollapsibleStatusPanel item={item} showBottomConnector={!isLast} />
        </StatusItem>
    ),
}

const ChatStatusMessage = ({ items, connectToFollowUp = false, withoutVisual = false }: ChatStatusMessageProps) => {
    if (!items.length) return null

    return (
        <div className="chat-status-message">
            {items.map((item, i) => {
                const isLast = i === items.length - 1
                const render = RENDERERS[item.kind ?? 'row']
                return (
                    <Fragment key={item.id}>
                        {render({ item, isLast, connectFollowUp: isLast && connectToFollowUp, withoutVisual })}
                    </Fragment>
                )
            })}
        </div>
    )
}

export default ChatStatusMessage
